import type { HistoryEntry } from '@ctcells/contract'
import { t } from '@ctcells/i18n'

import { orchestrator } from '../clients.ts'
import { ApiError } from '../errors.ts'
import { keys } from '../keys.ts'
import { normalizeList, type Listed } from '../page.ts'
import { createQuery, queryParams } from '../query.ts'
import {
  EVENT_LEDGERS,
  mergeHistoryDesc,
  type HistoryLedger,
  type HistoryListArgs,
} from './admin.ts'

/**
 * `GET /history` 를 **끝까지** 읽는다 — 작업별 이벤트 로그 내려받기(eric 2026-10-02)가 쓴다.
 * 거름(`event_type` · `batch_id` · `from` · `to`)은 받은 그대로 매 쪽에 싣는다 — 이 함수는 원장을 고르지 않는다.
 *
 * 🔴 **훅이 아니다.** 누를 때 한 번 도는 일이라 쿼리 캐시에 남기지 않는다(같은 키로 화면의 쪽 조회와 섞이면
 *    「1쪽만 받은 캐시」가 「전부」로 읽힌다). 폴링도 아니다 — 사람이 누른 횟수만큼 돈다(DP-01).
 *
 * 🔴 **`to` 를 고정해서 부른다.** 서버 차례는 `ts desc` 이고(`app/services/history.py` `list_`) 쪽 나눔은
 *    `offset` 이다. 도는 작업은 읽는 사이에도 행이 앞에 붙어 **뒤 쪽이 밀린다** — 같은 행이 두 번 오거나 빠진다.
 *    누른 시각을 `to` 로 박으면 그 뒤에 생긴 행이 아예 안 들어와 쪽이 흔들리지 않는다.
 *    ⚠ 같은 `ts` 의 행이 쪽 경계에 걸치면 서버가 차례를 보장하지 않는다(정렬 열이 `ts` 하나뿐) — 이건
 *      서버 반출(`POST /exports` 의 작업별 데이터셋 · 백엔드 요청)이 정답이다.
 *
 * 끝 판정: 받은 행이 `limit` 보다 적다 · 또는 모은 수가 `total_count` 에 닿았다 · 또는 빈 쪽.
 * 🔴 **`maxPages` 를 넘으면 잘린 결과를 «성공»으로 돌려주지 않는다** — `truncated` 로 알린다.
 */
export interface FetchAllHistoryOptions {
  /** 한 번에 받는 행 수(서버 상한 500) */
  pageSize: number
  /** 쪽 수 안전장치 */
  maxPages: number
  /** 시험이 갈아 끼운다. 기본은 오케스트레이터 클라이언트 */
  fetchPage?: (args: HistoryListArgs) => Promise<Listed<HistoryEntry>>
}

export type FetchAllHistoryResult =
  | { kind: 'complete'; items: HistoryEntry[] }
  /** 안전장치에 걸렸다. `total` 은 서버가 준 전체 수(모르면 `null`) */
  | { kind: 'truncated'; fetched: number; total: number | null }

async function defaultFetchPage(args: HistoryListArgs): Promise<Listed<HistoryEntry>> {
  return normalizeList<HistoryEntry>(
    await orchestrator.request({ method: 'GET', path: '/history', query: queryParams(args) }),
    args,
  )
}

export async function fetchAllHistory(
  args: Omit<HistoryListArgs, 'limit' | 'offset'>,
  options: FetchAllHistoryOptions,
): Promise<FetchAllHistoryResult> {
  const { pageSize, maxPages, fetchPage = defaultFetchPage } = options
  const items: HistoryEntry[] = []
  let total: number | null = null

  for (let page = 0; page < maxPages; page += 1) {
    const got = await fetchPage({ ...args, limit: pageSize, offset: items.length })
    total = got.total_count
    items.push(...got.items)
    const short = got.items.length < pageSize
    const reached = total !== null && items.length >= total
    if (got.items.length === 0 || short || reached) return { kind: 'complete', items }
  }
  return { kind: 'truncated', fetched: items.length, total }
}

export const useAllHistory = createQuery<
  Omit<HistoryListArgs, 'limit' | 'offset'>,
  Listed<HistoryEntry>
>({
  key: (args) => keys.history.list({ ...args, scope: 'all' }),
  fetch: async (args) => {
    const result = await fetchAllHistory(
      { ...args, to: args.to ?? new Date().toISOString() },
      { pageSize: 500, maxPages: 50 },
    )
    if (result.kind !== 'complete')
      throw new ApiError({
        status: 0,
        message: t('error.historyIncomplete'),
      })
    return { items: result.items, total_count: result.items.length, limit: null, offset: null }
  },
})

/**
 * 「이벤트」 원장들(기본 `EVENT_LEDGERS` — `event` · `batch_record` · 작업별 내려받기는 감사까지 셋)을 **각각 끝까지** 읽어
 * 시각 내림차순으로 섞는다.
 * 작업별 이벤트 로그 내려받기가 쓴다. 원장을 고르는 것은 이 함수다(`event_type` 은 받지 않는다).
 *
 * 🔴 2026-10-08(v0.9.4) 배치 기록이 `event_log` 에서 `batch_record` 로 옮겨 갔다 — 옛 작업은 `event` 에, 새 작업은
 *    `batch_record` 에 있다(`useEventHistory` 머리말). 하나만 읽으면 한쪽 작업의 파일이 비거나 반쪽이 된다.
 * 🔴 **하나라도 잘리면 전체를 «잘림»으로 돌려준다** — 반쪽 파일을 «완성»으로 내지 않는다. `fetched` 는 둘의 합,
 *    `total` 은 둘의 합(하나라도 모르면 `null`).
 * 원장은 차례로 읽는다(동시에 두 줄기로 긁지 않는다 — 서버 한 곳에 쪽 요청이 겹치지 않게).
 */
export async function fetchAllEventHistory(
  args: Omit<HistoryListArgs, 'limit' | 'offset' | 'event_type'> & {
    /** 읽을 원장 — 기본 `EVENT_LEDGERS`(작업별 내려받기는 `BATCH_EVENT_LEDGERS` 셋을 넘긴다) */
    ledgers?: readonly HistoryLedger[]
  },
  options: FetchAllHistoryOptions,
): Promise<FetchAllHistoryResult> {
  const { ledgers = EVENT_LEDGERS, ...rest } = args
  const results: FetchAllHistoryResult[] = []
  for (const event_type of ledgers)
    results.push(await fetchAllHistory({ ...rest, event_type }, options))

  const complete = results.filter(
    (result): result is Extract<FetchAllHistoryResult, { kind: 'complete' }> =>
      result.kind === 'complete',
  )
  if (complete.length === results.length)
    return { kind: 'complete', items: mergeHistoryDesc(complete.map((result) => result.items)) }

  let fetched = 0
  let total: number | null = 0
  for (const result of results) {
    const count = result.kind === 'complete' ? result.items.length : result.fetched
    fetched += count
    const known = result.kind === 'complete' ? count : result.total
    total = total === null || known === null ? null : total + known
  }
  return { kind: 'truncated', fetched, total }
}
